import { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BodyCopy } from "../components/BodyCopy";
import { Button, GradientText, Layout } from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useCashDepositStore } from "../store/useCashDepositStore";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { useCashDepositContext } from "../hooks/useCashDepositContext";
import { APP_ROUTES } from "../constants";
import { accentInkOnSurface } from "../utils/accentInkOnSurface";
import { getFundingLocations } from "../services/api";
import type { IFundingMerchant } from "../interfaces/cash-deposit";
import { getEvents, safeCapture } from "../analytics";
import {
  isFundingPolicyReady,
  requiresCustomerToChooseChain,
} from "../utils/funding-policy";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { Navbar } from "../components/Navbar";

const MapPinIcon = ({ color, size = 16 }: { color: string; size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M12 21s7-4.35 7-11a7 7 0 1 0-14 0c0 6.65 7 11 7 11Z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="10" r="2.5" fill={color} />
  </svg>
);

const StoreLogo = ({ merchant }: { merchant: IFundingMerchant }) => {
  const [failed, setFailed] = useState(false);

  if (failed || !merchant.logoUrl) {
    return (
      <span className="stores-card-fallback" aria-hidden>
        {merchant.name}
      </span>
    );
  }

  return (
    <img
      src={merchant.logoUrl}
      alt={merchant.name}
      className="stores-card-logo"
      onError={() => setFailed(true)}
    />
  );
};

/**
 * Stores picker for the cash-deposit flow. Shown only when Superadmin InComm
 * policy sets requireCustomerToChooseChain. Choosing a chain pins merchantId for
 * the barcode POST; the scanned token is still network-wide.
 */
export const StoresPage = memo(() => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const { context } = useCashDepositContext();
  const {
    merchants,
    selectedMerchantId,
    setMerchants,
    setSelectedMerchantId,
    setInstrument,
  } = useCashDepositStore();
  const colorConfig = theme.whiteView;
  const {
    background,
    backgroundBtn,
    backgroundIcon,
    textColorBtn,
    bodyCopy,
    footerColor,
    title,
  } = colorConfig;
  const partnerColors = useSessionStore(
    (s) =>
      s.session.data?.partner?.theme?.colors as
        | Record<string, string>
        | undefined,
  );
  const accentSolid = accentInkOnSurface(partnerColors, background, bodyCopy);
  const { redirectTo } = useRouteWithToken();

  useEffect(() => {
    if (
      isFundingPolicyReady(context) &&
      !requiresCustomerToChooseChain(context)
    ) {
      redirectTo(APP_ROUTES.CASH_STORES_PAGE, { replace: true });
    }
  }, [context, redirectTo]);

  useEffect(() => {
    safeCapture(getEvents().SCREEN_VIEW("merchant_picker"));
  }, []);

  // Load the funding-location directory from the corridor (once). When this
  // picker is shown, selection is an explicit tap — never a pre-pick.
  useEffect(() => {
    let cancelled = false;
    if (merchants.length > 0) return;
    (async () => {
      try {
        const data = await getFundingLocations();
        if (cancelled) return;
        setMerchants(data);
      } catch {
        // Non-fatal: the list simply stays empty.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [merchants.length, setMerchants]);

  const selectMerchant = (merchantId: string) => {
    // Any barcode already issued belongs to the previous chain, so the
    // selection change invalidates it locally too.
    setInstrument(null);
    if (merchantId === selectedMerchantId) {
      setSelectedMerchantId(null);
      return;
    }
    setSelectedMerchantId(merchantId);
    safeCapture(getEvents().MERCHANT_SELECTED, {
      merchant_id: merchantId,
    });
  };

  const themeVars = {
    "--store-accent-btn": backgroundBtn,
    "--store-accent-solid": accentSolid,
    "--store-card-border": bodyCopy,
    "--store-card-surface": background,
  } as React.CSSProperties;

  return (
    <>
      <AuraLayout colorConfig={colorConfig} className="stores-section-page">
        <Layout.Header style={themeVars}>
          <Navbar
            title={t("stores.viewMap")}
            align="right"
            color={bodyCopy}
            onBackClick={() =>
              redirectTo(APP_ROUTES.CASH_DEPOSIT_LANDING_PAGE)
            }
            onIconClick={() =>
              redirectTo(`${APP_ROUTES.STORES_MAP_PAGE}?from=stores`)
            }
            icon={<MapPinIcon color={backgroundIcon} />}
          />
        </Layout.Header>

        <Layout.Content className="stores-section" style={themeVars}>
        <section className="stores-copy">
          <GradientText as="h1" textColor={bodyCopy} className="stores-title">
            {t("stores.title")}
          </GradientText>
          <BodyCopy textColor={bodyCopy} className="stores-subtitle">
            {t("stores.subtitle")}
          </BodyCopy>
        </section>

        <section
          className="stores-list"
          role="listbox"
          aria-label={t("stores.merchantsLabel")}
        >
          <ScrollFadeIndicator color={bodyCopy} />
          {merchants.map((merchant) => {
            const selected = merchant.merchantId === selectedMerchantId;
            return (
              <button
                key={merchant.merchantId}
                type="button"
                role="option"
                aria-selected={selected}
                className={`stores-card${
                  selected ? " stores-card--selected" : ""
                }`}
                onClick={() => selectMerchant(merchant.merchantId)}
              >
                <StoreLogo merchant={merchant} />
              </button>
            );
          })}
        </section>
        </Layout.Content>

        <Layout.Footer>
        <section className="valid-cards">
          <section className="valid-cards-container stores-disclaimer-block">
            <BodyCopy className="stores-disclaimer-line" textColor={footerColor}>
              {t("stores.disclaimerLine1")} {t("stores.disclaimerLine2")}{" "}
              <GradientText
                as="span"
                className="stores-terms-link"
                textColor={title}
                onClick={() => redirectTo(APP_ROUTES.LEGAL_PAGE)}
              >
                {t("stores.termsLink")}
              </GradientText>
              .
            </BodyCopy>
          </section>
          <section className="footer-section">
            <Button
              size="large"
              background={backgroundBtn}
              color={textColorBtn}
              disabled={!selectedMerchantId}
              onClick={() => {
                if (!selectedMerchantId) return;
                redirectTo(APP_ROUTES.CASH_STORES_PAGE);
              }}
            >
              {t("stores.cta")}
            </Button>
          </section>
        </section>
        </Layout.Footer>
      </AuraLayout>
    </>
  );
});
